import React from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { MydawaLogo } from "@/components/MydawaLogo";
import AskAI from "@/components/AskAI";
import { GranularityProvider, GranularityBar } from "@/context/GranularityContext";
import {
  LayoutDashboard,
  Megaphone,
  Ticket,
  Users,
  ShieldAlert,
  Banknote,
  ScrollText,
  MessageSquare,
  LogOut,
  Loader2,
  Database,
  Trophy,
  Clock,
  Gauge,
  Zap,
  Bell,
  UserPlus,
  Settings,
  Siren,
  PhoneCall,
  BookOpen,
} from "lucide-react";

const LINKS = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, id: "nav-dashboard" },
  { to: "/admin/alerts", icon: Bell, label: "Alerts", id: "nav-alerts" },
  { to: "/admin/incidents", icon: Siren, label: "AI Incidents", id: "nav-incidents" },
  { to: "/admin/cs-handoffs", icon: PhoneCall, label: "CS Handoffs", id: "nav-cs-handoffs" },
  { to: "/admin/whatsapp", icon: MessageSquare, label: "WhatsApp Inbox", id: "nav-whatsapp" },
  { to: "/admin/program", icon: Settings, label: "Program Config", id: "nav-program" },
  { to: "/admin/promo-pool", icon: Ticket, label: "Promo Pool", id: "nav-promo" },
  { to: "/admin/contacts", icon: UserPlus, label: "Contacts", id: "nav-contacts" },
  { to: "/admin/referrals", icon: Users, label: "Referrals", id: "nav-referrals" },
  { to: "/admin/leaderboard", icon: Trophy, label: "Leaderboard", id: "nav-leaderboard" },
  { to: "/admin/fraud", icon: ShieldAlert, label: "Fraud Queue", id: "nav-fraud" },
  { to: "/admin/payouts", icon: Banknote, label: "Payouts", id: "nav-payouts" },
  { to: "/admin/nps", icon: Gauge, label: "NPS", id: "nav-nps" },
  { to: "/admin/dwh", icon: Database, label: "DWH Simulator", id: "nav-dwh" },
  { to: "/admin/smoke", icon: Zap, label: "Smoke Runner", id: "nav-smoke" },
  { to: "/admin/scheduler", icon: Clock, label: "Scheduler", id: "nav-scheduler" },
  { to: "/admin/templates", icon: Megaphone, label: "Templates", id: "nav-templates" },
  { to: "/admin/audit", icon: ScrollText, label: "Audit Log", id: "nav-audit" },
  { to: "/admin/help", icon: BookOpen, label: "User Guide", id: "nav-help" },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#66cc33]" />
      </div>
    );
  }
  if (!user || user === false || !user.role) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <GranularityProvider>
      <div className="flex min-h-screen bg-[#fafcfa]">
        <aside className="hidden w-64 shrink-0 border-r border-[#e2e8e2] bg-white md:flex md:flex-col">
          <div className="border-b border-[#e2e8e2] px-5 py-4">
            <MydawaLogo />
          </div>
          <nav className="flex-1 space-y-1 overflow-y-auto p-3">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={l.id}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                    isActive
                      ? "bg-[#eaf8de] text-[#3f7c21]"
                      : "text-[#5c665a] hover:bg-[#fafcfa] hover:text-[#1a1c19]"
                  }`
                }
              >
                <l.icon className="h-4 w-4" />
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-[#e2e8e2] p-3">
            <div className="mb-2 rounded-lg bg-[#fafcfa] px-3 py-2">
              <div className="text-xs text-[#5c665a]">Signed in as</div>
              <div className="truncate text-sm font-semibold text-[#1a1c19]">{user.email}</div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-[#66cc33]">{user.role}</div>
              {user.role === "finance" && (
                <div className="mt-1 rounded bg-[#fff0f5] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-[#c01974]">
                  read-only
                </div>
              )}
            </div>
            <button
              onClick={async () => { await logout(); nav("/admin/login"); }}
              className="admin-btn-ghost w-full"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </aside>

        <div className="md:hidden">
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#e2e8e2] bg-white px-4 py-3">
            <MydawaLogo />
            <button
              onClick={async () => { await logout(); nav("/admin/login"); }}
              className="admin-btn-ghost"
              data-testid="logout-btn-mobile"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto border-b border-[#e2e8e2] bg-white px-3 py-2 text-xs">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `shrink-0 rounded-full px-3 py-1.5 font-semibold ${
                    isActive ? "bg-[#66cc33] text-white" : "bg-[#fafcfa] text-[#5c665a]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>

        <main className="flex-1 overflow-x-hidden p-5 md:p-8">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-[#e2e8e2] pb-4">
            <GranularityBar />
          </div>
          <Outlet />
        </main>

        <AskAI />
      </div>
    </GranularityProvider>
  );
}
